export const VOTIQ_CUSTOM_TRIGGER_STYLE_ID = 'votiq-custom-trigger-styles';

export const VOTIQ_CUSTOM_TRIGGER_PREVIEW_STYLE_ID =
    'votiq-custom-trigger-preview-styles';

export const VOTIQ_WIDGET_ROOT_ID = 'votiq-widget-root';

export const WIDGET_CUSTOM_BUTTON_CLASS = 'votiq-custom-trigger';

export type CustomTriggerShape = 'pill' | 'rounded' | 'square';

export type CustomTriggerStyleConfig = {
    color: string;
    shape: CustomTriggerShape;
};

const DEFAULT_COLOR = '#0a5c58';

function normalizeColor(color: string | undefined): string {
    const value = (color ?? DEFAULT_COLOR).trim();

    return /^#[0-9A-Fa-f]{6}$/.test(value) ? value : DEFAULT_COLOR;
}

function normalizeShape(shape: string | undefined): CustomTriggerShape {
    if (shape === 'rounded' || shape === 'square' || shape === 'pill') {
        return shape;
    }

    return 'pill';
}

function borderRadiusForShape(shape: CustomTriggerShape): string {
    switch (shape) {
        case 'square':
            return '6px';
        case 'rounded':
            return '12px';
        default:
            return '9999px';
    }
}

function textColor(backgroundColor: string): string {
    const hex = backgroundColor.replace('#', '');

    if (hex.length !== 6) {
        return '#ffffff';
    }

    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.72 ? '#001715' : '#ffffff';
}

export function resolveCustomTriggerStyleConfig(
    partial?: Partial<CustomTriggerStyleConfig> | null,
): CustomTriggerStyleConfig {
    return {
        color: normalizeColor(partial?.color),
        shape: normalizeShape(partial?.shape),
    };
}

/** Markup class for install snippets (styles still apply to any `[data-votiq]`). */
export function buildCustomTriggerClassAttr(): string {
    return WIDGET_CUSTOM_BUTTON_CLASS;
}

export function buildCustomTriggerStyleCss(
    config: Partial<CustomTriggerStyleConfig> | null,
): string {
    const style = resolveCustomTriggerStyleConfig(config);
    const labelColor = textColor(style.color);
    const radius = borderRadiusForShape(style.shape);

    const triggerSelector = '[data-votiq]';

    return `
:root {
  --votiq-custom-trigger-bg: ${style.color};
  --votiq-custom-trigger-fg: ${labelColor};
  --votiq-custom-trigger-radius: ${radius};
}
${triggerSelector},
button${triggerSelector},
a${triggerSelector} {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--votiq-custom-trigger-fg) !important;
  background-color: var(--votiq-custom-trigger-bg) !important;
  border: none;
  border-radius: var(--votiq-custom-trigger-radius) !important;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 23, 21, 0.12);
  transition: filter 0.15s ease, transform 0.15s ease;
}
${triggerSelector}:hover,
button${triggerSelector}:hover,
a${triggerSelector}:hover {
  filter: brightness(1.05);
}
`;
}

/**
 * Install HTML — `data-votiq` is required; class is optional (embed styles all triggers).
 */
export function buildCustomTriggerButtonHtml(label: string): string {
    const text = label.trim() || 'Your Button';
    const safe = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    return `<button type="button" data-votiq class="${WIDGET_CUSTOM_BUTTON_CLASS}" aria-label="${safe}">
  ${safe}
</button>`;
}

/** Ensures customer triggers have the marker class (styles target `[data-votiq]` either way). */
export function decorateCustomTriggerElements(): void {
    if (typeof document === 'undefined') {
        return;
    }

    document.querySelectorAll<HTMLElement>('[data-votiq]').forEach((element) => {
        if (element.closest(`#${VOTIQ_WIDGET_ROOT_ID}`)) {
            return;
        }

        element.classList.add(WIDGET_CUSTOM_BUTTON_CLASS);
    });
}

export function ensureCustomTriggerStyles(
    config: Partial<CustomTriggerStyleConfig> | null,
    styleElementId: string = VOTIQ_CUSTOM_TRIGGER_STYLE_ID,
): void {
    if (typeof document === 'undefined') {
        return;
    }

    let styleEl = document.getElementById(
        styleElementId,
    ) as HTMLStyleElement | null;

    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleElementId;
        document.head.appendChild(styleEl);
    }

    styleEl.textContent = buildCustomTriggerStyleCss(config);
}

export function removeCustomTriggerStyles(
    styleElementId: string = VOTIQ_CUSTOM_TRIGGER_STYLE_ID,
): void {
    document.getElementById(styleElementId)?.remove();
}
